import React, { useState, useEffect } from "react";
import { Modal, Table, Tag, Button, Col, Row, DatePicker, Select } from "antd";
import { EditOutlined } from "@ant-design/icons";
import moment from "moment";
import { getApi, putApi } from "redux/apis"; // Added putApi for updates
import { toastText } from "utils/utils";
import styles from "./index.module.scss";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const { Column } = Table;
interface Props {
  visible: boolean;
  onClose: () => void;
  record: any;
  onEdit: (item: any) => void;
  isAdmin: boolean;
  isEmployee: boolean;
  onItemUpdate?: () => void; // New prop for notifying parent of updates
  serviceCenters: any;
}

const InventoryDetailsModal: React.FC<Props> = ({
  isAdmin,
  isEmployee,
  visible,
  onClose,
  record,
  onEdit,
  onItemUpdate, // New prop
  serviceCenters,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [groupedData, setGroupedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [bulkArrivalDate, setBulkArrivalDate] = useState<any>(null);
  const [canFetchMore, setCanFetchMore] = useState(true);
  const [localUpdates, setLocalUpdates] = useState<Record<string, any>>({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [isMarkingAsReceived, setIsMarkingAsReceived] = useState(false); // Track bulk update loading
  const { companyRoles } = useSelector(
    (state: any) => state?.userProfile?.data
  );
  const [sorter, setSorter] = useState<{
    columnKey?: string;
    order?: "ascend" | "descend";
  }>({});

  console.log("company roles", serviceCenters);

  // Removed auto-close functionality
  // const checkAndCloseModalIfAllReceived = async () => {
  //   // Wait a bit for state to update
  //   setTimeout(() => {
  //     const hasInTransitItems = groupedData.some(item => {
  //       const displayStatus = getDisplayStatus(item);
  //       return displayStatus === "In Transit" || displayStatus === "Partial";
  //     });

  //     // If no items are in transit, close the modal
  //     if (!hasInTransitItems && groupedData.length > 0) {
  //       setTimeout(() => {
  //         onClose();
  //       }, 1000); // Small delay for better UX
  //     }
  //   }, 500);
  // };

  useEffect(() => {
    if (selectedRowKeys.length == 0 && bulkArrivalDate != null) {
      // Reset bulk date if no items are selected
      setBulkArrivalDate(null);
    }
  }, [selectedRowKeys]);

  // Group data by itemNumber and sum quantities
  const groupDataByItem = (rawData: any[]) => {
    // First, filter data by service center
    const serviceSpecificData = rawData.filter(
      (item) => item.serviceCenter === record.serviceCenter
    );

    const grouped = serviceSpecificData.reduce((acc: any, item: any) => {
      const key = item.itemNumber;

      if (!acc[key]) {
        acc[key] = {
          ...item,
          itemNumber: key,
          quantity: 0, // Initialize quantity for this service center
          totalQuantity: 0, // Keep track of total if needed
          items: [],
          hasReceivedItems: false,
          hasInTransitItems: false,
          serviceCenter: item.serviceCenter,
        };
      }

      // Sum quantity only for the specific service center
      acc[key].quantity += parseInt(item.quantity) || 0;
      acc[key].items.push(item);

      // Update status flags
      const currentStatus =
        item.status === "SHIPPED" ? "IN_TRANSIT" : item.status;
      if (currentStatus === "RECEIVED") {
        acc[key].hasReceivedItems = true;
      } else {
        acc[key].hasInTransitItems = true;
      }

      return acc;
    }, {});

    return Object.values(grouped);
  };

  // Handle individual date change - sync with bulk date if applicable
  const handleDateChange = (date: dayjs.Dayjs | null, itemNumber: string) => {
    // Clear bulk date when individual date is changed
    if (date) {
      setBulkArrivalDate(null);
    }

    setLocalUpdates((prev) => ({
      ...prev,
      [itemNumber]: {
        ...prev[itemNumber],
        arrivalDate: date,
        // Flag to indicate this is an individual update
        isIndividualUpdate: true,
      },
    }));
  };

  // Update the handleBulkDateChange function
  const handleBulkDateChange = (date: any) => {
    setBulkArrivalDate(date);

    if (selectedRowKeys.length > 0) {
      setLocalUpdates((prev) => {
        const updated = { ...prev };
        selectedRowKeys.forEach((itemNumber) => {
          // Update regardless of individual flag if item is selected
          updated[itemNumber] = {
            ...updated[itemNumber],
            arrivalDate: date,
            isIndividualUpdate: false,
          };
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    if (selectedRowKeys.length == 0 && bulkArrivalDate != null) {
      setBulkArrivalDate(null);
    }
  }, [selectedRowKeys]);
  console.log("selected row key", selectedRowKeys);
  const getStatusColor = (status: string) => {
    switch (status) {
      case "RECEIVED":
      case "Received":
        return "green";
      case "IN_TRANSIT":
      case "In Transit":
        return "orange";
      case "SHIPPED":
        return "blue";
      case "Partial":
        return "orange";
      default:
        return "default";
    }
  };

  const getDisplayStatus = (groupedItem: any) => {
    const localUpdate = localUpdates[groupedItem.itemNumber];
    if (localUpdate?.status) {
      return localUpdate.status === "IN_TRANSIT" ? "In Transit" : "Received";
    }

    // Determine status based on grouped items
    if (groupedItem.hasReceivedItems && groupedItem.hasInTransitItems) {
      return "Partial";
    } else if (groupedItem.hasReceivedItems) {
      return "Received";
    } else {
      return "In Transit";
    }
  };

  // Check if Mark as Received button should be enabled
  const isMarkAsReceivedEnabled = () => {
    if (isMarkingAsReceived) {
      return false;
    }

    // If bulk update mode (items are selected)
    if (selectedRowKeys.length > 0) {
      // Check if bulk date is set
      if (!bulkArrivalDate) {
        return false;
      }

      // Validate selected items
      return selectedRowKeys.every((itemNumber) => {
        const groupedItem = groupedData.find(
          (item) => item.itemNumber === itemNumber
        );
        const displayStatus = getDisplayStatus(groupedItem);
        return displayStatus !== "Received";
      });
    }

    // For individual updates (no selections)
    return groupedData.some((item) => {
      const localUpdate = localUpdates[item.itemNumber];
      const displayStatus = getDisplayStatus(item);

      // Enable if any non-received item has an individual date set
      return (
        displayStatus !== "Received" &&
        localUpdate?.isIndividualUpdate &&
        localUpdate.arrivalDate
      );
    });
  };

  const columns = [
    {
      title: "Item#",
      dataIndex: "itemNumber",
      key: "itemNumber",
    },
    {
      title: "Quantity",
      dataIndex: "quantity", // Changed from totalQuantity to quantity
      key: "quantity",
      render: (_: any, record: any) => {
        // Show quantity for the specific service center
        return <span>{record.quantity}</span>;
      },
    },
    {
      title: "Arrival Date",
      dataIndex: "arrivalDate",
      key: "arrivalDate",
      render: (value: dayjs.Dayjs, groupedItem: any) => {
        const localUpdate = localUpdates[groupedItem.itemNumber];
        const isSelected = selectedRowKeys.includes(groupedItem.itemNumber);

        // Use bulk date if item is selected, otherwise use individual date
        const dateValue = isSelected
          ? bulkArrivalDate
          : localUpdate?.arrivalDate;

        const displayStatus = getDisplayStatus(groupedItem);
        const isReceivedItem = displayStatus === "Received";

        return (
          <DatePicker
            value={dateValue}
            format="MM/DD/YYYY"
            onChange={(date) => handleDateChange(date, groupedItem.itemNumber)}
            disabledDate={(current) =>
              current && current.isAfter(dayjs(), "day")
            }
            placeholder="Select date"
            disabled={
              disable || isMarkingAsReceived || isReceivedItem || isSelected
            }
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_: any, groupedItem: any) => {
        const displayStatus = getDisplayStatus(groupedItem);
        const color = getStatusColor(displayStatus);

        return <Tag color={color}>{displayStatus}</Tag>;
      },
    },
  ];

  // whenever we open on a new record, reset everything
  useEffect(() => {
    if (visible && record) {
      setData([]);
      setGroupedData([]);
      setPage(1);
      setCanFetchMore(true);
      setSorter({});
      setLocalUpdates({});
      setTotalRecords(0);
      setIsMarkingAsReceived(false);
      setBulkArrivalDate(null);
      setSelectedRowKeys([]);
      fetchAllData();
    }
  }, [visible, record]);

  // Removed auto-close effect
  // useEffect(() => {
  //   if (visible && groupedData.length > 0) {
  //     checkAndCloseModalIfAllReceived();
  //   }
  // }, [groupedData, visible]);

  // Sync bulk date when selection changes
  useEffect(() => {
    if (bulkArrivalDate && selectedRowKeys.length > 0) {
      setLocalUpdates((prev) => {
        const updated = { ...prev };
        selectedRowKeys.forEach((itemNumber) => {
          updated[itemNumber] = {
            ...updated[itemNumber],
            arrivalDate: bulkArrivalDate,
            isIndividualUpdate: false,
          };
        });
        return updated;
      });
    }
  }, [selectedRowKeys]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const allData: any[] = [];
      let currentPage = 1;
      let hasMore = true;
      const pageSize = 1000;

      while (hasMore) {
        const query: any = {
          page: currentPage,
          pageSize: pageSize,
          serviceCenterId: record.serviceCenterId,
          salesOrderNumber: [record.salesOrderNumber],
          itemNumber: [record.itemNumber],
          status: record.status,
          serviceCenter: serviceCenters, // Add service center filter
          // deliveryDate: record.deliveryDate
        };

        if (sorter.columnKey && sorter.order) {
          query.sortBy = sorter.columnKey;
          query.sortOrder = sorter.order === "ascend" ? "asc" : "desc";
        }

        console.log("query", query);
        const resp = await getApi("/inventory/recieve", query);
        const newRows = resp.data.data.data;
        console.log("newRows", newRows);
        const total = resp.data.data.total || 0;

        allData.push(...newRows);
        setTotalRecords(total);

        hasMore = currentPage * pageSize < total;
        currentPage++;
      }

      console.log("All data fetched:", allData.length, "records");
      setData(allData);

      const grouped = groupDataByItem(allData);
      setGroupedData(grouped);

      setCanFetchMore(false);
    } catch (err: any) {
      toastText(
        err.response?.data?.error?.message || "Failed to load details",
        "error"
      );
      setCanFetchMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Updated bulk action to persist to backend - only called when "Mark as Received" is clicked
  const handleBatchAction = async () => {
    try {
      let itemsToUpdate: string[] = [];

      // Determine which items to update based on selection mode
      if (selectedRowKeys.length > 0) {
        // Bulk update mode
        itemsToUpdate = selectedRowKeys.filter((itemNumber) => {
          const groupedItem = groupedData.find(
            (item) => item.itemNumber === itemNumber
          );
          return getDisplayStatus(groupedItem) !== "Received";
        });
      } else {
        // Individual update mode
        itemsToUpdate = groupedData
          .filter((item) => {
            const localUpdate = localUpdates[item.itemNumber];
            const displayStatus = getDisplayStatus(item);
            return (
              displayStatus !== "Received" &&
              localUpdate?.isIndividualUpdate &&
              localUpdate.arrivalDate
            );
          })
          .map((item) => item.itemNumber);
      }

      if (itemsToUpdate.length === 0) {
        toastText("No items selected for update", "warning");
        return;
      }

      // Validate that all items to update have arrival dates
      const itemsWithoutDates = itemsToUpdate.filter((itemNumber) => {
        const localUpdate = localUpdates[itemNumber];
        return !localUpdate?.arrivalDate && !bulkArrivalDate;
      });

      if (itemsWithoutDates.length > 0) {
        toastText("Please set arrival dates for all selected items", "warning");
        return;
      }

      setIsMarkingAsReceived(true);
      try {
        // Update each item in the backend
        const updatePromises = itemsToUpdate.map(async (itemNumber) => {
          const groupedItem = groupedData.find(
            (item) =>
              item.itemNumber === itemNumber &&
              item.serviceCenter === record.serviceCenter
          );

          if (!groupedItem) {
            throw new Error(`Item ${itemNumber} not found`);
          }

          // Use individual date if set, otherwise use bulk date
          const localUpdate = localUpdates[itemNumber];
          const arrivalDate = localUpdate?.isIndividualUpdate
            ? localUpdate.arrivalDate
            : localUpdate?.arrivalDate || bulkArrivalDate;

          if (!arrivalDate) {
            throw new Error(`No arrival date set for item ${itemNumber}`);
          }

          const updateData = groupedItem.items
            .filter((item) => item.serviceCenter === record.serviceCenter)
            .map((item) => ({
              id: item.id,
              arrivalDate: arrivalDate.format("YYYY-MM-DD"),
              status: "RECEIVED",
              quantity: item.quantity,
              ids: [item.id],
            }));

          return putApi(`/inventory/create`, updateData);
        });

        await Promise.all(updatePromises);

        toastText(
          `Successfully marked ${itemsToUpdate.length} items as received`,
          "success"
        );

        // Update local state to reflect changes immediately
        setLocalUpdates((prev) => {
          const updated = { ...prev };
          itemsToUpdate.forEach((itemNumber) => {
            updated[itemNumber] = {
              ...updated[itemNumber],
              status: "RECEIVED",
            };
          });
          return updated;
        });

        // Update grouped data to show new status
        setGroupedData((prevGrouped) =>
          prevGrouped.map((item) => {
            if (itemsToUpdate.includes(item.itemNumber)) {
              return {
                ...item,
                hasReceivedItems: true,
                hasInTransitItems: false,
                status: "RECEIVED",
              };
            }
            return item;
          })
        );

        // Notify parent component that items have been updated
        if (onItemUpdate) {
          onItemUpdate();
        }

        // Clear selections and bulk date after successful update
        setSelectedRowKeys([]);
        setBulkArrivalDate(null);

        // Refresh data to ensure consistency with backend
        // setTimeout(() => {
        //   fetchAllData();
        // }, 500);
      } catch (err: any) {
        toastText(
          err.response?.data?.error?.message || "Failed to update items",
          "error"
        );
        console.error("Error in bulk update:", err);
      } finally {
        setIsMarkingAsReceived(false);
      }
    } catch (error) {
      // ... error handling
    }
  };

  const handleTableChange = (_: any, __: any, sorterParams: any) => {
    setSorter({
      columnKey: sorterParams.columnKey,
      order: sorterParams.order,
    });
    fetchAllData();
  };
  const disable = companyRoles[0]?.role.roleName === "3nStar Employee 1";

  return (
    <Modal
      open={visible}
      title={`Service Center: ${record?.serviceCenter}`}
      onCancel={onClose}
      footer={null}
      width={1000}
      centered
      className={styles["inventory-modal"]}
    >
      {/* Bulk date picker - show when items are selected */}
      {selectedRowKeys.length > 0 && (
        <Row>
          <Col span={24}>
            <Row align="middle">
              <Col span={24}>
                <div className={styles["modal-header"]}>
                  <span>Bulk Update Arrival Date:</span>
                  <DatePicker
                    placeholder="Bulk Update Arrival Date"
                    value={bulkArrivalDate}
                    onChange={handleBulkDateChange}
                    format="MM/DD/YYYY"
                    disabledDate={(currentDate) =>
                      currentDate && currentDate.isAfter(dayjs(), "day")
                    }
                    style={{ width: "18vw" }}
                    disabled={disable || isMarkingAsReceived}
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      <Table
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys: any) => {
            // Filter out received items from selection
            const validKeys = keys.filter((key: string) => {
              const groupedItem = groupedData.find(
                (item) => item.itemNumber === key
              );
              const displayStatus = getDisplayStatus(groupedItem);
              return displayStatus !== "Received";
            });
            setSelectedRowKeys(validKeys);
          },
          getCheckboxProps: (record) => {
            const displayStatus = getDisplayStatus(record);
            return {
              disabled: isMarkingAsReceived || displayStatus === "Received",
            };
          },
        }}
        rowKey={(record) => record.itemNumber}
        dataSource={groupedData}
        pagination={false}
        columns={columns}
        className="bg-white"
        style={{ width: "100%", padding: "1.5rem 0px" }}
        scroll={{ y: 300 }}
        loading={loading}
        onChange={handleTableChange}
      />

      <div className={styles["inventory-modal__footer"]}>
        <Button
          size="large"
          onClick={onClose}
          className="cancel-button"
          disabled={isMarkingAsReceived}
          style={{ marginRight: 10 }}
        >
          Cancel
        </Button>
        {/* Always show Mark as Received button, but enable/disable based on conditions */}
        <Button
          type="primary"
          size="large"
          className={styles["inventory-modal__mark-as-received-button"]}
          onClick={handleBatchAction}
          disabled={disable || !isMarkAsReceivedEnabled()}
          loading={isMarkingAsReceived}
          style={{ fontSize: 15 }}
        >
          Mark as Received
        </Button>
      </div>
    </Modal>
  );
};

export default InventoryDetailsModal;
